import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import React, { useContext, useMemo, useState } from 'react';
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import theme from '../../../theme';
import { Dashboard } from '../../../types/files';
import UpdateDealOwnerModal from '../../modals/dashboard-details/UpdateDealOwnerModal';
import { DashboardContext } from '../../../contexts/DashboardContext';

const useStyles = makeStyles((theme) => ({
    masonryElement: {
        display: 'inline-block',
        width: '100%',
        height: 'auto',
        overflow: 'hidden',
        padding: 'unset',
        border: 'unset',
        background: 'transparent',
        transition: 'all 300ms ease',
        "&:hover": {
            border: `unset !important`,
            background: 'transparent !important',
            boxShadow: 'unset !important',
        },
    },
    answerContainer: {
        width: 'auto',
        height: 'auto',
        padding: 8,
        border: `1px transparent solid`,
        borderRadius: 12,
    },
    answerHover: {
        width: '96%',
        marginRight: 2,
    },
    title: {
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    label: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    sublabel: {
        fontWeight: '500',
        color: theme.colors.neutral['500'],
    },
    icon: {
        width: '24px !important',
        height: '24px !important',
    }
}));

const DealOwner: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { isPublicView } = useContext(DashboardContext);
    const { dashboardOwner } = useBackwardsCompatible();

    const [ownerModalOpen, setOwnerModalOpen] = useState<boolean>(false);
    const [hoverOwner, setHoverOwner] = useState<boolean>(false);

    const owner = useMemo(() => dashboardOwner(dashboard), [dashboard, dashboardOwner]);

    return (<>
        <Box className={classes.masonryElement}>
            <Stack direction="column" spacing="6px" alignItems="flex-start" width="100%"
                onMouseLeave={() => setHoverOwner(false)}>
                <Typography className={classes.title}>{'Deal Owner'}</Typography>
                <Stack direction="row" className={classnames(classes.answerContainer,
                        hoverOwner && classes.answerHover
                    )} alignItems="center" justifyContent="space-between" width="100%"
                    onMouseEnter={() => setHoverOwner(true)}>
                    <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="flex-start" width="100%" minHeight="24px">
                        {(!!owner?.name || !!owner?.email) ? (<>
                            <PersonOutlineOutlinedIcon sx={{ width: 24, height: 24, fill: "rgb(163, 167, 172)" }} />
                            <Stack direction="column" spacing="4px" alignItems="flex-start" justifyContent="center">
                                {!!owner?.name && (
                                    <Typography className={classes.label}>
                                        {owner?.name}
                                    </Typography>
                                )}
                                {!!owner?.email && (
                                    <Typography className={classnames(classes.label, classes.sublabel)}>
                                        {owner?.email}
                                    </Typography>
                                )}
                            </Stack>
                        </>) : (
                            <Typography className={classnames(classes.label, classes.sublabel)} ml={-1}>
                                {'No deal owner'}
                            </Typography>
                        )}
                    </Stack>
                    {(!isPublicView && hoverOwner) && (
                        <Stack direction="row" alignItems="stretch" justifyContent="flex-end">
                            <IconButton disableRipple
                                onClick={() => setOwnerModalOpen(true)}
                                sx={{ padding: 'unset !important'}}>
                                <EditOutlinedIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Box>
        {ownerModalOpen && (
            <UpdateDealOwnerModal
                isOpen={ownerModalOpen}
                dashboard={dashboard}
                onClose={() => setOwnerModalOpen(false) } />
        )}
    </>);
}

export default DealOwner;
